import { useRef, useState } from "react";
import html2pdf from "html2pdf.js";
import FiltersBar from "./components/FiltersBar";
import OverviewTab from "./components/OverviewTab";
import OrdersTab from "./components/OrdersTab";
import SalesTab from "./components/SalesTab";
import CustomersTab from "./components/CustomersTab";
import TeamTab from "./components/TeamTab";
import { serviceSummary } from "@/mocks/salesReports";

const tabs = [
  { id: "overview", label: "نظرة عامة" },
  { id: "orders", label: "الطلبات" },
  { id: "sales", label: "المبيعات" },
  { id: "customers", label: "العملاء" },
  { id: "team", label: "الفريق" },
];

export default function SalesReports() {
  const [tab, setTab] = useState("overview");
  const [period, setPeriod] = useState("هذا الشهر");
  const [service, setService] = useState("all");
  const [toast, setToast] = useState<string | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const reportRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<number | null>(null);

  const showToast = (message: string) => {
    setToast(message);
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => setToast(null), 2800);
  };

  const handleExportExcel = () => {
    const header = ["الخدمة", "الطلبات", "الإيرادات", "متوسط القيمة", "نسبة الإلغاء"];
    const rows = serviceSummary.map((r) => [r.service, r.orders, r.revenue, `${r.avg} ر.س`, r.cancel]);
    const csv = `\uFEFF${[header, ...rows].map((r) => r.join(",")).join("\r\n")}`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `sales-report-${tab}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast("تم تصدير التقرير إلى Excel");
  };

  const handleExportPdf = async () => {
    const el = reportRef.current;
    if (!el || generatingPdf) return;
    setGeneratingPdf(true);
    try {
      await html2pdf()
        .set({
          margin: [8, 8, 10, 8],
          filename: `sales-report-${tab}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
          jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
        })
        .from(el)
        .save();
      showToast("تم تصدير التقرير إلى PDF");
    } catch {
      showToast("تعذّر إنشاء ملف PDF، حاول مرة أخرى");
    } finally {
      setGeneratingPdf(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-[24px] font-bold text-ghasla-text-heading md:text-[28px]">تقارير المبيعات</h1>
        {generatingPdf && (
          <span className="flex items-center gap-2 text-sm text-ghasla-text-secondary">
            <i className="ri-loader-4-line animate-spin" />
            جارٍ تجهيز ملف PDF...
          </span>
        )}
      </div>

      {/* Tabs */}
      <div className="mt-5 flex gap-6 overflow-x-auto border-b border-[#E4F5F5]">
        {tabs.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`relative shrink-0 cursor-pointer whitespace-nowrap pb-3 pt-1 text-sm transition-colors ${
                active
                  ? "font-semibold text-ghasla-text-heading"
                  : "text-ghasla-text-secondary hover:text-ghasla-text-primary"
              }`}
            >
              {t.label}
              {active && (
                <span className="absolute inset-x-0 -bottom-px h-[3px] rounded-full bg-gradient-to-l from-[#0D6E6E] to-[#00C4CF]" />
              )}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <FiltersBar
        period={period}
        onPeriodChange={setPeriod}
        service={service}
        onServiceChange={setService}
        onExportExcel={handleExportExcel}
        onExportPdf={handleExportPdf}
      />

      {period === "مخصص" && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <div className="flex h-11 items-center gap-2 rounded-xl border border-[#E4EFEF] bg-white px-3 text-sm text-ghasla-text-secondary">
            <i className="ri-calendar-line" />
            <span className="whitespace-nowrap">من</span>
            <input type="date" className="cursor-pointer bg-transparent text-sm text-ghasla-text-primary outline-none" />
          </div>
          <div className="flex h-11 items-center gap-2 rounded-xl border border-[#E4EFEF] bg-white px-3 text-sm text-ghasla-text-secondary">
            <i className="ri-calendar-line" />
            <span className="whitespace-nowrap">إلى</span>
            <input type="date" className="cursor-pointer bg-transparent text-sm text-ghasla-text-primary outline-none" />
          </div>
        </div>
      )}

      {/* Report content */}
      <div ref={reportRef} dir="rtl" className="mt-5 rounded-[20px]">
        <div className="hidden bg-white p-6 text-lg font-bold text-ghasla-text-heading print:block">
          تقارير المبيعات — {tabs.find((t) => t.id === tab)?.label}
        </div>
        {tab === "overview" && <OverviewTab />}
        {tab === "orders" && <OrdersTab />}
        {tab === "sales" && <SalesTab />}
        {tab === "customers" && <CustomersTab />}
        {tab === "team" && <TeamTab />}
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[100] -translate-x-1/2 rounded-full bg-[#007A85] px-5 py-2.5 text-sm font-medium text-white shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}